import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { mergeDeep, omit } from 'remeda';
import { ResourceType } from 'utility/types/dataProvider';
import { z } from 'zod';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { PostEditorialExportParams } from 'providers/contexts/extensions/ExportImport';
import { hasBetaProgram } from 'utility/experiments';

// --------------------------------------------------------------
// Schemas / Validation
// --------------------------------------------------------------

const RESOURCE_TYPES = [
  'user',
  'subscription',
  'editorial',
  'dashboard',
  'transactions',
  'wire',
  'emailreach_contacts',
  'wire',
  'emailreach_contacts',
] as const satisfies readonly ResourceType[];

const NxtExportItemSchema = z.object({
  exportId: z.string(),
  siteId: z.string(),
  timestamp: z.number(),
  resourceType: z.enum(RESOURCE_TYPES),
  isQueued: z.boolean(),
  isRunning: z.boolean(),
  isReady: z.boolean(),
  downloadStarted: z.boolean(),
  // Optional properties for dashboard exports
  id: z.string().optional(),
  title: z.string().optional(),
  accountId: z.string().optional().nullable(),
  listId: z.string().optional().nullable(),
  // Optional error tracking properties
  hasError: z.boolean().optional(),
  errorMessage: z.string().optional(),
  retryCount: z.number().optional(),
});

// --------------------------------------------------------------
// Types
// --------------------------------------------------------------

type NxtExportItem = {
  exportId: string;
  isQueued: boolean;
  isRunning: boolean;
  isReady: boolean;
  downloadStarted: boolean;
  resourceType: ResourceType;
  timestamp: number;
  siteId: string;
  // Optional properties for dashboard exports
  id?: string;
  title?: string;
  accountId?: string | null;
  listId?: string | null;
  // Error tracking
  hasError?: boolean;
  errorMessage?: string;
  retryCount?: number;
};

type NxtExportStorageSlice = {
  exports: Record<string, NxtExportItem>;
};

type NxtExportStorageData = {
  [siteKey: string]: NxtExportStorageSlice;
};

type NxtExportContextType = {
  exports: Record<string, NxtExportItem>;
  handleExportSubmit: (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    selectedItemIds: string[] | GridRowSelectionModel,
  ) => Promise<boolean>;
  addExport: (
    exportId: string,
    resourceType: ResourceType,
    additionalProps?: Partial<NxtExportItem>,
  ) => void;
  updateExport: (exportId: string, updates: Partial<NxtExportItem>) => void;
  removeExport: (exportId: string) => void;
  clearAllExports: () => void;
  exportCount: number;
  readyCount: number;
  hasActiveExports: boolean;
  hasReadyExports: boolean;
  allExportsReady: boolean;
};

// type is mantines hook argument "StorageProperties<T>"
// but was unable to import it here it directly from mantine
type StorageProps<T> = Parameters<typeof useLocalStorage<T>>[0];

// --------------------------------------------------------------
// Constants & Helpers
// --------------------------------------------------------------

const NXT_EXPORT_LS_KEY = 'nxtExportStorage';
const EXPORT_RESTORED_LS_KEY = 'isRestoredExportSession';
const EXPORT_LAST_SITE_KEY = 'lastActiveExportSite';
const EXPORT_AUTOHIDE_TIME = 24 * 60 * 60 * 1000; // 24h

const DEFAULT_STORAGE_DATA: NxtExportStorageData = {};
const STORAGE_HOOK_CONFIG: StorageProps<NxtExportStorageData> = {
  key: NXT_EXPORT_LS_KEY,
  defaultValue: DEFAULT_STORAGE_DATA,
  sync: true,
};

/**
 * Read the data from localStorage without subscribing to it.
 * Avoids unnecessary re-renders / useEffect loops.
 */
function getNxtExportStorage(): NxtExportStorageData {
  const storage = readLocalStorageValue<NxtExportStorageData>(STORAGE_HOOK_CONFIG);
  return storage;
}

function normalizeExportSelection(
  selectedItemIds: string[] | GridRowSelectionModel,
): string[] {
  if (Array.isArray(selectedItemIds)) {
    return selectedItemIds.filter((id): id is string => Boolean(id));
  }

  if (!selectedItemIds || selectedItemIds.type !== 'include') {
    return [];
  }

  return Array.from(selectedItemIds.ids)
    .map((id) => String(id))
    .filter(Boolean);
}

// --------------------------------------------------------------
// Hooks and Context Provider
// --------------------------------------------------------------
const NxtExportContext = createContext<NxtExportContextType | undefined>(undefined);

export const useNxtExport = (): NxtExportContextType => {
  const ctx = useContext(NxtExportContext);
  if (!ctx) {
    throw new Error('useNxtExport must be used within a NxtExportProvider');
  }
  return ctx;
};

interface NxtExportProviderProps {
  children: ReactNode;
}

export const NxtExportProvider = memo(({ children }: NxtExportProviderProps) => {
  const { activeSite, sessionConfig, userInfo } = useCosmos();
  const showNotifications = hasBetaProgram(sessionConfig, userInfo);
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const { t } = useTranslation();
  // top nav local storage state
  const [nxtExportStorage, setNxtExportStorage] =
    useLocalStorage<NxtExportStorageData>(STORAGE_HOOK_CONFIG);

  // normalize active site key for consistent storage access
  const siteKey = useMemo(() => activeSite?.toLowerCase()?.trim(), [activeSite]);

  // on site switch / app load prune expired exports
  useEffect(() => {
    if (!siteKey) return;

    // read without subscribing to avoid effect loops
    const full = getNxtExportStorage();
    // get the exports slice for this site
    const siteExports = full?.[siteKey]?.exports ?? {};
    // filter (autohide) expired exports by timestamp
    // keep only those that are still valid
    const now = Date.now();
    const validExports: Record<string, NxtExportItem> = {};
    for (const [id, item] of Object.entries(siteExports)) {
      if (now < item?.timestamp + EXPORT_AUTOHIDE_TIME) {
        validExports[id] = { ...item, siteId: siteKey };
      }
    }

    // update the exports map with just valid items
    setNxtExportStorage((prev) => ({
      ...prev,
      [siteKey]: { exports: validExports },
    }));

    // manage restored session flags
    const lastSiteLS = localStorage.getItem(EXPORT_LAST_SITE_KEY);
    const hasValidExports = Boolean(Object.keys(validExports).length);
    // if we have valid exports, set the restored session flag
    // and update the last active site key
    if (hasValidExports) {
      localStorage.setItem(EXPORT_RESTORED_LS_KEY, 'true');
      localStorage.setItem(EXPORT_LAST_SITE_KEY, siteKey);
    }
    // if no valid exports and this was the last site
    // clear the restored session flags
    if (!hasValidExports && lastSiteLS === siteKey) {
      localStorage.removeItem(EXPORT_RESTORED_LS_KEY);
      localStorage.removeItem(EXPORT_LAST_SITE_KEY);
    }
  }, [siteKey, setNxtExportStorage]);

  // this sites exports derived from the nxtExportStorage
  // sorted from newest to oldest based on timestamp
  const exports = useMemo(() => {
    const siteExports = nxtExportStorage?.[siteKey]?.exports ?? {};
    const sortedEntries = Object.entries(siteExports).sort(
      ([, a], [, b]) => b.timestamp - a.timestamp,
    );
    return Object.fromEntries(sortedEntries);
  }, [nxtExportStorage, siteKey]);

  // --- Actions handlers --------------------------------------------------

  /**
   * Add a new export item to the context.
   */
  const addExport = useCallback(
    (
      exportId: string,
      resourceType: ResourceType,
      additionalProps?: Partial<NxtExportItem>,
    ) => {
      const now = Date.now();
      const newItem: NxtExportItem = {
        exportId: exportId,
        isQueued: true,
        isRunning: false,
        resourceType: resourceType,
        isReady: false,
        downloadStarted: false,
        timestamp: now,
        siteId: siteKey,
        ...additionalProps,
      };
      // merge in the new export
      setNxtExportStorage((prev) => {
        const merged = mergeDeep(prev, {
          [siteKey]: { exports: { [exportId]: newItem } },
        });
        return merged;
      });
    },
    [siteKey, setNxtExportStorage],
  );

  /**
   * Update an existing exports fields with new data.
   */
  const updateExport = useCallback(
    (exportId: string, updates: Partial<NxtExportItem>) => {
      const original = exports?.[exportId];
      // nothing to update
      if (!original) {
        console.warn(`[updateExport] Export ${exportId} does not exist in storage.`);
        return;
      }
      // merge existing and new updates
      const merged = mergeDeep(original, updates);
      // validate the merge result against the schema
      const parseResult = NxtExportItemSchema.safeParse(merged);
      if (!parseResult.success) {
        console.warn('[updateExport] Update failed validation:', parseResult?.error);
        return;
      }
      // if validation passed, we have a valid updated item
      const validNxtExportItem = parseResult?.data;
      // set back into storage
      setNxtExportStorage((prev) =>
        mergeDeep(prev, {
          [siteKey]: { exports: { [exportId]: validNxtExportItem } },
        }),
      );
    },
    [siteKey, exports, setNxtExportStorage],
  );

  /**
   * Remove an export by its ID.
   */
  const removeExport = useCallback(
    (exportId: string) => {
      setNxtExportStorage((prev) => {
        const current = prev?.[siteKey]?.exports ?? {};
        const rest = omit(current, [exportId]);
        return {
          ...prev,
          [siteKey]: { exports: rest },
        };
      });
    },
    [siteKey, setNxtExportStorage],
  );

  /**
   * Clear all exports for the active site.
   */
  const clearAllExports = useCallback(() => {
    setNxtExportStorage((prev) => ({
      ...prev,
      [siteKey]: { exports: {} },
    }));
  }, [siteKey, setNxtExportStorage]);

  // --- Computed context values ------------------------------------------

  const exportCount = Object.keys(exports).length;
  const readyCount = Object.values(exports).filter((e) => e.isReady).length;
  const hasActiveExports =
    Object.values(exports).filter((e) => !e.isReady && (e.isRunning || e.isQueued))
      .length > 0;
  const hasReadyExports = readyCount > 0;
  const allExportsReady = hasActiveExports && readyCount === exportCount;

  const hasEditorialExports = useMemo(
    () =>
      Object.values(exports).some(
        (e) =>
          !e.isReady && (e.isRunning || e.isQueued) && e.resourceType === 'editorial',
      ),
    [exports],
  );

  const { data: exportStatusData } = useQuery({
    queryKey: ['exportstatus', exports, hasEditorialExports],
    queryFn: async () => {
      // Filter imports that need status checking
      const editorialExports = Object.values(exports).filter(
        (e) =>
          !e.isReady && (e.isRunning || e.isQueued) && e.resourceType === 'editorial',
      );

      if (editorialExports.length === 0) {
        return [];
      }

      const exportStatus = await Promise.all(
        editorialExports.map(async (exportItem) => {
          try {
            const response = await dataProvider.getExportStatus(exportItem.exportId);
            const statusData = response?.data || null;
            return { exportId: exportItem.exportId, statusData: statusData };
          } catch (error) {
            console.error(
              `[NxtExport] Error fetching status for export ${exportItem.exportId}:`,
              error,
            );
            return {
              exportId: exportItem.exportId,
              status: 'error',
            };
          }
        }),
      );
      return exportStatus; // Return the array of statuses for all channels
    },
    // Enable query only if there are hasImportFiles that have a status of 'importing'
    enabled: hasEditorialExports,
    refetchInterval: 5000, // Refetch every 5 seconds
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0, // No caching for polling query
    staleTime: 0, // Always consider stale for immediate refetch
  });

  useEffect(() => {
    if (exportStatusData) {
      // Update imports based on the wire import status data
      exportStatusData.forEach((item) => {
        if (item && item.statusData) {
          const { exportId, statusData } = item;
          // Update the import status based on the API response
          const updates: Partial<NxtExportItem> = {};

          if (statusData.running) {
            updates.isQueued = false;
            updates.isRunning = true;
          }

          if (statusData.complete) {
            // Notify user of successful import
            notify(t('blox.export.msg.export_ready'), {
              type: 'success',
            });
            updates.isQueued = false;
            updates.isRunning = false;
            updates.isReady = true;
          }

          // Apply updates if any
          if (Object.keys(updates).length > 0) {
            updateExport(exportId, updates);
          }
        }
      });
    }
  }, [exportStatusData, exports, t, updateExport, notify]);

  const handleExportSubmit = useCallback(
    async (
      event: React.MouseEvent<HTMLLIElement, MouseEvent>,
      selectedItemIds: string[] | GridRowSelectionModel,
    ) => {
      event.preventDefault();

      const uuids = normalizeExportSelection(selectedItemIds);
      if (uuids.length === 0) {
        notify(t('blox.export.msg.export_error'), { type: 'error' });
        return false;
      }

      try {
        const params: PostEditorialExportParams = {
          body: {
            uuid: uuids,
          },
        };

        const result = await dataProvider.createExportTask(params);

        if (result.status === 'success') {
          if (!showNotifications) {
            // Add export to TopNavContext with 'editorial' resource type
            addExport(result.data.id, 'editorial');
          }

          // If complete immediately, mark as ready
          if (result.data.complete && !showNotifications) {
            updateExport(result.data.id, { isReady: true });
          }
          notify(t('blox.export.msg.export_success'), { type: 'info' });
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error creating export task:', error);
        notify(t('blox.export.msg.export_error'), { type: 'error' });
        return false;
      }
    },
    [dataProvider, notify, t, addExport, updateExport, showNotifications],
  );

  const contextValue: NxtExportContextType = useMemo(
    () => ({
      exports,
      addExport,
      updateExport,
      removeExport,
      clearAllExports,
      exportCount,
      readyCount,
      hasActiveExports,
      hasReadyExports,
      allExportsReady,
      handleExportSubmit,
    }),
    [
      exports,
      addExport,
      updateExport,
      removeExport,
      clearAllExports,
      exportCount,
      readyCount,
      hasActiveExports,
      hasReadyExports,
      allExportsReady,
      handleExportSubmit,
    ],
  );

  return (
    <NxtExportContext.Provider value={contextValue}>{children}</NxtExportContext.Provider>
  );
});

NxtExportProvider.displayName = 'NxtExportProvider';
